/**
 * Admin V3 — Operations / Notifications — Search / filter bar
 *
 * Layout (left → right):
 *   [Channel filter] [Status filter] [Search input] [Result counter]
 *
 * Channel filter is mapped client-side to which endpoint(s) the page fetches
 * from (SMS, Email, or both in parallel).
 *
 * Status filter is server-side (?status= query param on each endpoint).
 *
 * Search input is client-side over loaded page (recipient OR booking_reference).
 *
 * NO CSV (no backend endpoint).
 * NO resend button (mutation, Phase 3+).
 */

import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { notificationStatusMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

// Sentinel for the empty option (Select requires a non-empty value).
const ALL_SENTINEL = '__all__';

const STATUS_VALUES = ['pending', 'sent', 'failed', 'skipped'];

const OperationsNotificationsSearchBar = ({
  channel,
  onChannelChange,
  status,
  onStatusChange,
  search,
  onSearchChange,
  resultsCount,
  isFilterActive,
  loading,
  locale = 'fr',
}) => {
  const statusSelectValue = status || ALL_SENTINEL;
  const handleStatus = (v) => onStatusChange(v === ALL_SENTINEL ? '' : v);

  const resultsCountText = t('operations.notifications.results.count', locale).replace('{n}', String(resultsCount));

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Channel filter */}
      <Select value={channel} onValueChange={onChannelChange}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-operations-notifications-channel-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('operations.notifications.filter.channelAll',   locale)}</SelectItem>
          <SelectItem value="sms">{t('operations.notifications.filter.channelSms',   locale)}</SelectItem>
          <SelectItem value="email">{t('operations.notifications.filter.channelEmail', locale)}</SelectItem>
        </SelectContent>
      </Select>

      {/* Status filter */}
      <Select value={statusSelectValue} onValueChange={handleStatus}>
        <SelectTrigger
          className="h-10 w-[180px] rounded-xl border-slate-200 bg-white text-sm text-slate-700"
          data-testid="adminv3-operations-notifications-status-filter"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SENTINEL}>{t('operations.notifications.filter.statusAll', locale)}</SelectItem>
          {STATUS_VALUES.map((v) => (
            <SelectItem key={v} value={v}>
              {notificationStatusMeta[v]?.label || v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Search (client-side) */}
      <div className="relative flex-1 max-w-md min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('operations.notifications.search.placeholder', locale)}
          data-testid="adminv3-operations-notifications-search"
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid="adminv3-operations-notifications-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Result counter */}
      {isFilterActive && !loading && (
        <span className="text-sm text-slate-400" data-testid="adminv3-operations-notifications-results-count">
          {resultsCountText}
        </span>
      )}
    </div>
  );
};

export default OperationsNotificationsSearchBar;
